import { getDocumentFirstFileId } from 'libs/core/hooks/src/Document/useDocumentFirstFile';
import type { ReactNode } from 'react';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import type {
	MySubmissionVoterCardAplication,
	PodaniDatovaZpravaIdDto,
} from '@gov-nx/api';
import { isVoterRegistrationSubmission, useAxiosInstance } from '@gov-nx/api';
import { GovError } from '@gov-nx/core/app';
import {
	useDataBoxMessagesLoader,
	useDocumentsDownload,
	useDownloadBlob,
	useMySubmissionDetailQuery,
	useProcessControl,
} from '@gov-nx/core/hooks';
import { useSubmissionConnection } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { compose, equals, is, prop } from '@gov-nx/core/types';
import { pickupDates } from '@gov-nx/module/service';
import type { PageCode } from '../../definitions/codes';
import { removeIdFromSubmissionName } from '../../epetice/seznam/utils';
import type {
	DataMessage,
	MySubmissionDetailContext,
	MySubmissionMessage,
} from './context.types';
import { useKeyValues } from './utils';

const isDataMessage = (
	message: PodaniDatovaZpravaIdDto
): message is DataMessage =>
	!!(message.datovaSchrankaId && message.datovaSchrankaId);

const MojePodaniDetailContext =
	createContext<Nullable<MySubmissionDetailContext>>(null);

interface MojePodaniDetailContextProviderProps {
	children: ReactNode;
	code: PageCode;
	submissionId: number;
}

export function MojePodaniDetailContextProvider({
	code,
	children,
	submissionId,
}: MojePodaniDetailContextProviderProps) {
	const { t } = useLocale(code);
	const { setControls, controls } = useProcessControl();
	const { handleDownloadBlob } = useDownloadBlob();
	const { prepare } = useDocumentsDownload();
	const { axiosInstance } = useAxiosInstance();
	const [documents, setDocuments] = useState<
		{
			documentId: number;
			souborId: string;
		}[]
	>([]);
	const [documentsLoading, setDocumentsLoading] = useState(false);

	const { error, isLoading, data } = useMySubmissionDetailQuery(submissionId);

	const submissionConnection = useSubmissionConnection({
		submissionId,
	});

	const dataMessages: DataMessage[] =
		data && Array.isArray(data.datoveZpravy) && data.datoveZpravy.length
			? data.datoveZpravy.filter(isDataMessage)
			: [];

	const messagesQuery = useDataBoxMessagesLoader({
		messageIds: dataMessages.map(prop('datovaZpravaId')),
		onError: () => setControls({ initialLoading: false }),
	});

	const messages: MySubmissionMessage[] = dataMessages
		.map((dataMessage) => {
			const message = (messagesQuery.data ?? []).find(
				compose(equals(dataMessage.datovaZpravaId), prop('datovaZpravaId'))
			);
			if (!message) return null;

			return {
				message: message ?? null,
				isDeleted: !message,
				dataMessage,
			};
		})
		.filter(is);

	const documentsVariables = useCallback(async () => {
		setDocumentsLoading(true);
		const mapResult =
			data?.dokumenty?.map(async (document) => {
				if (!document.dokumentId) {
					return null;
				}
				const souborId = await getDocumentFirstFileId(axiosInstance, {
					documentId: document.dokumentId,
				});

				if (!souborId) {
					return null;
				}

				return { documentId: document.dokumentId, souborId };
			}) ?? [];
		const result = (await Promise.all(mapResult)).filter(is);
		setDocuments(result);
		setDocumentsLoading(false);
	}, [axiosInstance, data?.dokumenty]);

	useEffect(() => {
		documentsVariables();
	}, [documentsVariables]);

	const isInitialLoading =
		isLoading ||
		(dataMessages.length > 0 && messagesQuery.isLoading) ||
		documentsLoading;

	const keyValues = useKeyValues(data);

	return (
		<MojePodaniDetailContext.Provider
			value={{
				code,
				controls: {
					...controls,
					initialError: error ? new GovError(error.message) : null,
					initialLoading: isInitialLoading,
				},
				submission: data,
				messages,
				submissionConnection,
				powerOfAttorney: handleDownloadBlob(() => {
					const params =
						data && isVoterRegistrationSubmission(data)
							? data.parametry
							: undefined;

					return {
						body: {
							kodPodani: 'ZADOST_VOLEBNI_PRUKAZ_PLNA_MOC',
							kodVoleb: params?.kod,
							kola: params ? getElectionRounds(params) : undefined,
							zpusobPrevzeti: params?.zpusobPrevzeti,
						},
					};
				}),
				documents: documents.map(prepare),
				strings: {
					submission: {
						name: data?.nazev
							? removeIdFromSubmissionName(data.nazev)
							: undefined,
					},
					voterRegistration:
						data && isVoterRegistrationSubmission(data)
							? {
									infoMessage: t('volicsky-prukaz.info-o-volicim-prukazu', {
										termin: pickupDates(data),
										adresaUradu: data.parametry?.vyzvednutiNaUradu
											? data.parametry?.vyzvednutiNaUradu
											: '&nbsp;',
									}),
								}
							: undefined,
				},
				keyValues,
			}}>
			{children}
		</MojePodaniDetailContext.Provider>
	);
}

export const useMojePodaniDetailContextInstance =
	(): MySubmissionDetailContext =>
		useContext(MojePodaniDetailContext) as MySubmissionDetailContext;

const getElectionRounds = (
	params: MySubmissionVoterCardAplication['parametry']
) => {
	if (params.kolo1zadost === 'true' && params.kolo2zadost === 'true') {
		return 'ALL';
	}
	if (params.kolo2zadost === 'true') {
		return 'II';
	}
	if (params.kolo1zadost === 'true') {
		return 'I';
	}
	return;
};
